import * as _ from 'underscore';
import { deepClone } from '../../ts/helpers/DeepClone';
import {
  Capabilities,
  CapabilitiesByAuthorizableType,
  ICapabilityInfo
} from '../types/Capabilities';
import { CapabilitySection } from '../types/CapabilitySections';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { Permission } from '../types/Permission';
import { PermissionRoleName } from '../types/PermissionRole';
import { PermissionsHasher } from './PermissionsHasher';
import { PermissionsHelper } from './PermissionsHelper';

interface PermissionRoleInfo {
  /* Permission values keyed by role name, then capability name */
  permissionValuesMap: _.Dictionary<_.Dictionary<Permission>>;

  /* Role names keyed by permissions hash */
  roleNamesByPermissionsHash: _.Dictionary<PermissionRoleName>;
}

export class PermissionRoleHelper {
  /* Hasher that helps with checking the matching role name */
  private permissionsHasher = new PermissionsHasher(Object.values(Capabilities));

  /* Mapping AuthorizableType to PermissionRoleInfo */
  private permissionRoleInfoMap: _.Dictionary<PermissionRoleInfo> = {};

  constructor(CapabilitySectionsByAuthorizableType: _.Dictionary<ReadonlyArray<CapabilitySection>>) {
    _.keys(CapabilitySectionsByAuthorizableType).forEach((authType: string) => {
      let permissionValuesMap: _.Dictionary<_.Dictionary<Permission>> = {};
      let roleNamesByPermissionsHash: _.Dictionary<PermissionRoleName> = {};
      const capabilitySections = CapabilitySectionsByAuthorizableType[authType];
      const unspecifiedRole: _.Dictionary<Permission> = {};
      const deniedRole: _.Dictionary<Permission> = {};
      CapabilitiesByAuthorizableType[authType].forEach((cap: Capabilities) => {
        unspecifiedRole[cap] = Permission.Unspecified;
        deniedRole[cap] = Permission.Denied;
      });

      // Add roles from capability sections
      let previousSectionAllowedCapabilities: string[] = [];
      capabilitySections.forEach((capabilitySection: CapabilitySection, index: number) => {
        permissionValuesMap[capabilitySection.name] = deepClone(unspecifiedRole);
        previousSectionAllowedCapabilities.forEach((capabilityName: string) => {
          permissionValuesMap[capabilitySection.name][capabilityName] = Permission.Allowed;
        });
        capabilitySection.capabilities.forEach((capability: ICapabilityInfo) => {
          permissionValuesMap[capabilitySection.name][capability.name] = Permission.Allowed;
          previousSectionAllowedCapabilities.push(capability.name);
        });
        const permissionsHash = this.permissionsHasher.hash(permissionValuesMap[capabilitySection.name]);
        roleNamesByPermissionsHash[permissionsHash] = capabilitySection.name;
      });

      // Add 'none' and 'denied' roles
      CapabilitiesByAuthorizableType[authType].forEach((cap: Capabilities) => {
        unspecifiedRole[cap] = Permission.Unspecified;
        deniedRole[cap] = Permission.Denied;
      });
      permissionValuesMap[PermissionRoleName.None] = unspecifiedRole;
      roleNamesByPermissionsHash[this.permissionsHasher.hash(unspecifiedRole)] = PermissionRoleName.None;
      permissionValuesMap[PermissionRoleName.Denied] = deniedRole;
      roleNamesByPermissionsHash[this.permissionsHasher.hash(deniedRole)] = PermissionRoleName.Denied;

      this.permissionRoleInfoMap[authType] = {
        permissionValuesMap,
        roleNamesByPermissionsHash,
      };
    });
  }

  /*
  * Gets the role name that describes this set of permissions on an authorizable type.
  * Returns 'custom' if there is no matching role name.
  */
  public getExplicitRoleForTypeAndPermissions(
    authType: string, permissions: IGranteePermissions
  ): PermissionRoleName {
    const permissionsValues = _.pick(
      PermissionsHelper.pluckPermissionsFromDictionary(
        PermissionsHelper.permissionsFromIGranteePermission(permissions, authType)
      ),
      CapabilitiesByAuthorizableType[authType],
    );
    const permissionsHash = this.permissionsHasher.hash(permissionsValues);
    const matchingRoleName = this.permissionRoleInfoMap[authType].roleNamesByPermissionsHash[permissionsHash];
    return matchingRoleName || PermissionRoleName.Custom;
  }

  /*
   * Gets the set of permission values for the authorizable type and role name.
   */
  public getPermissionsForTypeAndRole(authType: string, roleName: string): _.Dictionary<Permission> {
    return this.permissionRoleInfoMap[authType]?.permissionValuesMap[roleName];
  }

  /*
   * Gets the list of role names for the authorizable type
   */
  public getAvailableRolesForType(authType: string): PermissionRoleName[] {
    return _.values(this.permissionRoleInfoMap[authType].roleNamesByPermissionsHash);
  }
}
